"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { ArrowLeft } from "lucide-react";

export function TermsClient() {
  const siteName = process.env.NEXT_PUBLIC_SITE_NAME ?? "Meazora";
  const [activeSection, setActiveSection] = useState("agreement");

  useEffect(() => {
    const sections = document.querySelectorAll("section[id]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -60% 0px" }
    );

    sections.forEach((section) => observer.observe(section));
    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  const menuItems = [
    { id: "agreement", label: "1. Acceptance of Terms" },
    { id: "license", label: "2. Personal & Educational License" },
    { id: "use", label: "3. Acceptable Use Guidelines" },
    { id: "warranty", label: "4. Disclaimer of Warranties" },
    { id: "liability", label: "5. Limitation of Liability" },
    { id: "indemnity", label: "6. Indemnification" },
    { id: "property", label: "7. Intellectual Property" },
    { id: "dmca", label: "8. DMCA Copyright Notice" },
    { id: "governing", label: "9. Governing Law & Jurisdiction" },
    { id: "severability", label: "10. Severability" },
  ];

  return (
    <div className="max-w-6xl mx-auto py-10 px-4 md:px-6">
      {/* Back Link */}
      <div className="mb-10 animate-in fade-in slide-in-from-top-4 duration-300">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-800 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors group font-sans"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
          Back to Calculators
        </Link>
      </div>

      {/* Header */}
      <div className="border-b border-slate-200 dark:border-zinc-800 pb-8 mb-12 animate-in fade-in duration-500">
        <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-sky-600 dark:text-sky-400">
          Legal Framework
        </span>
        <h1 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-slate-55 mt-2 mb-4 tracking-tight leading-none font-sans">
          Terms of Service — Meazora
        </h1>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500 dark:text-zinc-500 font-mono">
          <span>Last updated: July 12, 2026</span>
          <span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-zinc-750" />
          <span>Read time: 3 min</span>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 items-start">
        {/* Sticky Sidebar */}
        <aside className="lg:col-span-1 sticky top-24 hidden lg:block select-none">
          <nav className="flex flex-col gap-3 font-sans text-xs">
            <span className="text-[9px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest pl-3 mb-1">
              Sections
            </span>
            {menuItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className={`py-1.5 pl-3 border-l-2 text-left font-medium transition-all duration-200 ${
                  activeSection === item.id
                    ? "border-sky-600 dark:border-sky-450 text-sky-600 dark:text-sky-400 font-semibold"
                    : "border-transparent text-slate-400 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-300"
                }`}
              >
                {item.label}
              </a>
            ))}
          </nav>
        </aside>

        {/* Content Area */}
        <div className="lg:col-span-3 space-y-16 font-sans text-slate-650 dark:text-zinc-300">
          <section id="agreement" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              1. Acceptance of Terms
            </h2>
            <p className="text-sm leading-relaxed">
              By accessing or utilizing {siteName} (the &quot;Service&quot;), you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not consent to these parameters, please discontinue use of our web calculators.
            </p>
          </section>

          <section id="license" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              2. Personal &amp; Educational License
            </h2>
            <p className="text-sm leading-relaxed">
              {siteName} grants you a personal, non-transferable, non-exclusive, revocable license to utilize our web-based material estimators. This access is provided entirely for personal educational modeling, project estimating, and general illustrative analysis.
            </p>
            <div className="border-l-2 border-sky-500/50 pl-4 py-1 my-4 bg-slate-50/50 dark:bg-zinc-900/30 rounded-r-lg text-xs text-slate-500 dark:text-zinc-400">
              Calculations are client-side mathematical simulations. They represent educational planning numbers and do not constitute formal contractor quotes, architectural specs, or structural code verification guarantees.
            </div>
          </section>

          <section id="use" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              3. Acceptable Use Guidelines
            </h2>
            <p className="text-sm leading-relaxed">
              To preserve the integrity and performance of {siteName}, you agree to adhere to the following rules:
            </p>
            <ul className="list-none pl-0 space-y-3 text-sm">
              <li className="flex items-start">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-500/70 mr-2.5 mt-2 shrink-0" />
                <span>You may not scrape or mirror calculator templates or style structures for commercial resale or lead-generation cloning.</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-500/70 mr-2.5 mt-2 shrink-0" />
                <span>You may not perform automated attacks, DDoS attempts, or activities that degrade web delivery performance.</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-500/70 mr-2.5 mt-2 shrink-0" />
                <span>Modifying the open-source client-side scripts is allowed for personal local experimentation under standard attribution licenses.</span>
              </li>
            </ul>
          </section>

          <section id="warranty" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              4. Disclaimer of Warranties
            </h2>
            <p className="text-sm leading-relaxed">
              The Service is provided on an &ldquo;AS IS&rdquo; and &ldquo;AS AVAILABLE&rdquo; basis, without warranties of any kind, either express or implied. To the fullest extent permitted by applicable law, {siteName} expressly disclaims all warranties, including but not limited to implied warranties of merchantability, fitness for a particular purpose, title, and non-infringement.
            </p>
            <p className="text-sm leading-relaxed">
              {siteName} does not warrant that the calculator results will be accurate, complete, reliable, current, or error-free. The material guidelines, pricing estimates, and code standards generated by the tools are mathematical models that depend on the data you supply and on assumptions that may not reflect your actual project conditions.
            </p>
          </section>

          <section id="liability" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              5. Limitation of Liability
            </h2>
            <p className="text-sm leading-relaxed">
              {siteName}, its creators, and open-source contributors shall not be held liable for any direct, indirect, incidental, special, or consequential damages resulting from your use of these estimators. This includes, but is not limited to, construction delays, material shortages, extra logistics fees, structural failure, property damage, code violations, or over-ordering costs.
            </p>
          </section>

          <section id="indemnity" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              6. Indemnification
            </h2>
            <p className="text-sm leading-relaxed">
              You agree to indemnify, defend, and hold harmless {siteName}, its creators, contributors, and affiliates from and against any and all claims, liabilities, damages, losses, and expenses — including reasonable legal and accounting fees — arising out of or in any way connected with your use of the Service or your violation of these Terms.
            </p>
          </section>

          <section id="property" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              7. Intellectual Property
            </h2>
            <p className="text-sm leading-relaxed">
              The user interface layouts, visual designs, unique math representations, and original educational copies are the exclusive property of {siteName}. Replication of the site branding, stylesheet signatures, or visual assets for commercial applications is prohibited without explicit prior consent.
            </p>
          </section>

          <section id="dmca" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              8. DMCA Copyright Notice
            </h2>
            <p className="text-sm leading-relaxed">
              {siteName} respects the intellectual property rights of others. If you believe that any content on this website infringes your copyright, please send a written notice containing: a description of the copyrighted work, identification of the infringing material, your contact information, a statement of good faith belief, and a statement under penalty of perjury that the information is accurate. Send DMCA notices to the contact address provided in our Privacy Policy. Upon receipt of a valid notice, we will promptly remove or disable access to the allegedly infringing content.
            </p>
          </section>

          <section id="governing" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              9. Governing Law &amp; Jurisdiction
            </h2>
            <p className="text-sm leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to its conflict of law provisions. Any dispute arising out of or relating to these Terms or your use of the Service shall be resolved exclusively in the state or federal courts located in Delaware, and you consent to the personal jurisdiction of such courts.
            </p>
          </section>

          <section id="severability" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              10. Severability
            </h2>
            <p className="text-sm leading-relaxed">
              If any provision of these Terms is found to be unenforceable or invalid by a court of competent jurisdiction, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
